import React, { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Ship,
    Wheat,
    FlaskConical,
    Mountain,
    Layers,
    Sprout,
    Package,
    Anchor,
    Globe2,
    FileSignature,
    Handshake,
    Network,
    Lock,
    ShieldCheck,
    ScrollText,
    UploadCloud,
    Send,
    Loader2,
    X,
    Phone,
    Mail,
    MapPin,
    ChevronRight,
    Banknote,
    ClipboardCheck,
    Gauge,
    Boxes,
    Shield,
    Flame,
    Pill,
    Building2,
    Briefcase,
    Cpu,
} from "lucide-react";
import { toast } from "sonner";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMMODITY_FOCUS = [
    {
        icon: Sprout,
        title: "Fertilizers",
        desc: "Structured fertilizer trade involving verified suppliers, institutional buyers, government-linked buyers, importers and mandate holders.",
        products: ["Urea", "DAP", "NPK", "MOP / Potash", "Ammonium Sulphate", "Rock Phosphate"],
        notes: ["CIF / CFR / FOB structures", "Monthly & annual supply contracts", "LC-backed payment structures", "Specification & origin review", "Inspection & shipment documentation"],
    },
    {
        icon: FlaskConical,
        title: "Sulphur",
        desc: "Sulphur-related trade opportunities — petroleum by-product, industrial, granular, lump and powder sulphur.",
        products: ["Granular sulphur", "Lump sulphur", "Powder sulphur", "Industrial sulphur", "Petroleum by-product sulphur"],
        notes: ["Purity & specification review", "Origin & availability discussion", "CFR / CIF delivery coordination", "Inspection & quality documentation", "Supplier-buyer procedure alignment"],
    },
    {
        icon: Wheat,
        title: "Agricultural Commodities",
        desc: "Agricultural commodity trade where quality, packing, inspection, pricing, delivery and payment terms require disciplined coordination.",
        products: ["Yellow maize", "Rice", "Wheat", "Pulses", "Legumes", "Oilseeds", "Edible oils", "Animal feed"],
        notes: ["Moisture & quality parameters", "Packing & shipment terms", "Port delivery coordination", "SGS / inspection support", "LC & payment alignment"],
    },
    {
        icon: Mountain,
        title: "Metals & Minerals",
        desc: "Metals and minerals opportunities requiring credible supply, origin clarity, inspection, logistics and commercial documentation.",
        products: ["Iron ore", "Copper", "Aluminium", "Manganese", "Chrome ore", "Coal-linked minerals", "Industrial minerals"],
        notes: ["Grade & specification review", "Origin & export documentation", "Inspection & assay documentation", "CIF / CFR / FOB procedures", "Institutional buyer engagement"],
    },
    {
        icon: Layers,
        title: "Industrial Raw Materials",
        desc: "Industrial raw materials required for manufacturing, chemicals, construction and processing industries.",
        products: ["Chemicals & feedstock", "Construction raw materials", "Industrial inputs", "Packaging raw materials", "Specialty raw materials"],
        notes: ["Technical specification alignment", "Supplier-buyer coordination", "Quality documentation", "Shipment & logistics alignment", "Commercial procedure support"],
    },
    {
        icon: Package,
        title: "Food & Consumer Trade Products",
        desc: "Selected food, consumer and trade products where structured sourcing, quality, compliance and cross-border delivery are required.",
        products: ["Dry fruits", "Pulses & legumes", "Food ingredients", "Processed food products", "Leather products", "Consumer trade goods"],
        notes: ["Supplier validation", "Product catalogue support", "Quality & packing review", "Import-export documentation", "Buyer requirement coordination"],
    },
];

const STRUCTURES = [
    "CIF transactions",
    "CFR transactions",
    "FOB transactions",
    "Ex-warehouse structures",
    "Spot cargo transactions",
    "Trial shipment structures",
    "Monthly supply contracts",
    "Annual supply contracts",
    "Government tender supply support",
    "PSU & institutional supply support",
    "LC-backed transactions",
    "Bank-secured procedures",
    "Inspection-led delivery procedures",
    "Mandate-based supplier-buyer facilitation",
];

const ROLE_POINTS = [
    "Supplier identification",
    "Buyer identification",
    "Exporter & importer coordination",
    "Mandate & authority validation",
    "Product specification review",
    "Price & commercial term alignment",
    "Origin & availability discussion support",
    "Transaction procedure review",
    "Commercial documentation coordination",
    "LOI / ICPO / SCO / FCO / SPA coordination",
    "CIS / KYC review support",
    "NCNDA / IMFPA / commission protection structuring",
    "Inspection procedure coordination",
    "Shipping & logistics discussion support",
    "LC & payment procedure alignment",
    "Government tender documentation support",
    "PSU & institutional procurement coordination",
    "Negotiation support",
    "Meeting coordination",
    "Transaction follow-up & execution support",
];

const PROCESS = [
    { n: "01", t: "Requirement or Supply Review", d: "Review of commodity requirement / supply offer — product, quantity, origin, specifications, packing, delivery terms, pricing expectations and transaction procedure." },
    { n: "02", t: "Counterparty Assessment", d: "Assessment of buyer, seller, mandate holder, exporter, importer or representative credibility and documentation readiness." },
    { n: "03", t: "Product & Specification Alignment", d: "Clarity on product grade, quality parameters, packing, quantity, origin, inspection standards and delivery location." },
    { n: "04", t: "Commercial Procedure Review", d: "Review of LOI, ICPO, SCO, FCO, SPA, payment terms, inspection process, shipment process and logistics steps." },
    { n: "05", t: "Documentation Coordination", d: "Structured documentation flow — company profiles, CIS, KYC, authorization, mandates, specs, inspection records and transaction documents." },
    { n: "06", t: "Negotiation & Stakeholder Coordination", d: "Structured communication, commercial negotiation, meeting coordination and alignment between serious counterparties." },
    { n: "07", t: "Execution Follow-Up", d: "Procedural follow-up, shipping coordination, inspection alignment, payment milestone tracking and commercial closure support." },
];

const COMPLIANCE_POINTS = [
    "Counterparty verification",
    "Mandate & authority validation",
    "Product origin clarity",
    "Specification & quality review",
    "Non-sanctioned origin awareness",
    "Inspection procedure clarity",
    "Payment & LC procedure alignment",
    "Bank document readiness",
    "Shipping & logistics documentation",
    "Import-export documentation awareness",
    "Confidentiality & non-circumvention protection",
    "Written communication trail",
    "Proper commercial documentation",
    "Jurisdiction-sensitive structuring",
];

const DOCUMENTS = [
    "Company Profile",
    "CIS / KYC",
    "Buyer Profile",
    "Supplier Profile",
    "LOI",
    "ICPO",
    "SCO",
    "FCO",
    "SPA",
    "Authorization Letter",
    "Mandate Letter",
    "Product Specification",
    "Certificate of Origin",
    "Product Availability Documents",
    "Inspection Report",
    "SGS / Intertek Report",
    "Bill of Lading",
    "Commercial Invoice",
    "Packing List",
    "Phytosanitary Certificate",
    "Fumigation Certificate",
    "Certificate of Analysis",
    "Proof of Product",
    "Proof of Funds",
    "Letter of Credit Draft",
    "NCNDA / IMFPA",
];

const WHO = [
    "Commodity suppliers",
    "International buyers",
    "Importers and exporters",
    "Trading houses",
    "Government-linked buyers",
    "Institutional procurement teams",
    "Fertilizer suppliers",
    "Agricultural commodity suppliers",
    "Metals and minerals suppliers",
    "Bulk cargo buyers",
    "Mandate holders",
    "Strategic representatives",
    "Logistics and shipping companies",
    "Warehousing partners",
    "Investors in trade infrastructure",
    "Buyers seeking structured supply",
    "Sellers seeking credible demand",
];

const RISK_PILLARS = [
    { icon: ShieldCheck, t: "Verified Counterparties", d: "Indravax engages with counterparties who can demonstrate identity, capacity and documentation discipline." },
    { icon: Gauge, t: "Realistic Pricing", d: "We work with offers and bids that reflect prevailing global market conditions and credible commercial terms." },
    { icon: ClipboardCheck, t: "Documented Authority", d: "We validate mandate chains, authorisation letters and procedural authority before progressing engagements." },
    { icon: Network, t: "Procedure Alignment", d: "We align buyer and seller procedures around payment, inspection, shipment and documentation milestones." },
];

const COUNTERPARTY_OPTIONS = [
    "Buyer", "Seller", "Supplier", "Manufacturer", "Exporter", "Importer",
    "Trading House", "Mandate Holder", "Government-Linked Entity",
    "Institutional Buyer", "Logistics Partner", "Investor", "Consultant", "Other",
];

const COMMODITY_CATEGORY_OPTIONS = [
    "Fertilizers", "Sulphur", "Agricultural Commodities",
    "Pulses & Legumes", "Dry Fruits", "Food Products", "Edible Oils", "Animal Feed",
    "Metals & Minerals", "Industrial Raw Materials", "Leather Products",
    "Chemicals", "Bulk Cargo", "Other",
];

const DELIVERY_OPTIONS = [
    "CIF", "CFR", "FOB", "Ex-Works", "Ex-Warehouse", "DAP", "DDP",
    "Spot Cargo", "Trial Shipment", "Monthly Contract", "Annual Contract", "Other",
];

const DOCUMENTS_AVAILABLE_OPTIONS = [
    "Company Profile", "CIS / KYC", "LOI", "ICPO", "SCO", "FCO",
    "SPA Draft", "Authorization Letter", "Mandate Letter",
    "Product Specification", "Certificate of Origin",
    "SGS / Inspection Report", "Bill of Lading", "Commercial Invoice", "Packing List",
    "Phytosanitary Certificate", "Fumigation Certificate", "Certificate of Analysis",
    "Proof of Product", "Proof of Funds", "LC Draft", "NCNDA / IMFPA",
    "Not Available Yet", "Other",
];

const TIMELINE_OPTIONS = [
    "Immediate (< 1 month)",
    "Short-term (1–3 months)",
    "Medium-term (3–6 months)",
    "Long-term (6+ months)",
    "Exploratory",
];

const OTHER_SECTORS = [
    { icon: Shield, title: "Defence & Aerospace", path: "/sectors/defence-aerospace" },
    { icon: Flame, title: "Oil, Gas & Energy", path: "/sectors" },
    { icon: Banknote, title: "Trade Finance & Transaction Structuring", path: "/sectors" },
    { icon: Cpu, title: "Technology & Emerging Systems", path: "/sectors" },
    { icon: Building2, title: "Infrastructure & Industrial Projects", path: "/sectors" },
    { icon: Pill, title: "Healthcare & Pharma", path: "/healthcare" },
    { icon: Briefcase, title: "Strategic Investments & Market Entry", path: "/sectors" },
];

const initialForm = {
    name: "", company: "", designation: "", country: "",
    email: "", phone: "",
    counterparty_type: COUNTERPARTY_OPTIONS[0],
    commodity_category: COMMODITY_CATEGORY_OPTIONS[0],
    product_name: "",
    product_specification: "",
    quantity: "",
    packing_details: "",
    delivery_terms: DELIVERY_OPTIONS[0],
    port_loading: "",
    port_discharge: "",
    origin: "",
    target_price: "",
    payment_terms: "",
    transaction_procedure: "",
    documents_available: DOCUMENTS_AVAILABLE_OPTIONS[0],
    timeline: TIMELINE_OPTIONS[0],
    message: "",
};

const ALLOWED_EXTS = [".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg"];
const MAX_BYTES = 10 * 1024 * 1024;

const SectorsCommodities = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
            toast.error("Unsupported file type. Allowed: PDF, DOC, DOCX, PNG, JPG.");
            e.target.value = "";
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error("File exceeds 10 MB limit.");
            e.target.value = "";
            return;
        }
        setFile(f);
    };

    const removeFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.product_name.trim()) {
            toast.error("Name, email and product name are required.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            if (file) fd.append("file", file);
            await axios.post(`${API}/commodity-enquiry`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Commodity enquiry received. A principal will respond within two working days.");
            setForm(initialForm);
            removeFile();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Unable to submit at this time. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-enter">
            <SEO
                title="Commodities & International Trade Advisory | Commodity Sourcing & Transaction Support"
                description="Indravax Strategic Advisory LLP supports structured commodity and international trade transactions across fertilizers, sulphur, agricultural commodities, metals, minerals, bulk cargo, sourcing, supplier-buyer validation, documentation, and cross-border trade advisory."
                path="/sectors/commodities-international-trade"
                keywords="commodities advisory India, international trade advisory, commodity sourcing India, commodity transaction support, fertilizer sourcing advisory, urea DAP NPK potash sourcing, sulphur trade advisory, agricultural commodity sourcing, metals and minerals trade advisory, bulk cargo transaction support, import export advisory India, supplier buyer facilitation, international commodity trade, CIF commodity transaction support, CFR commodity supply support, FOB commodity trade support, LC-backed commodity transactions, commodity documentation support, mandate-based commodity trade, cross-border trade advisory, global sourcing advisory, institutional procurement support, commodity supplier validation, commodity buyer validation"
            />

            {/* Schema.org */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "ProfessionalService",
                        "name": "Indravax Strategic Advisory LLP",
                        "url": "https://www.indravax.com",
                        "areaServed": "Global",
                        "serviceType": "Commodity Sourcing, International Trade Advisory, Supplier-Buyer Facilitation, Cross-Border Commodity Transaction Support",
                    },
                    {
                        "@type": "Service",
                        "name": "Commodities & International Trade Advisory",
                        "provider": { "@type": "Organization", "name": "Indravax Strategic Advisory LLP" },
                        "serviceType": "Commodity Sourcing & Transaction Support",
                    },
                    {
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.indravax.com/" },
                            { "@type": "ListItem", "position": 2, "name": "Sectors", "item": "https://www.indravax.com/sectors" },
                            { "@type": "ListItem", "position": 3, "name": "Commodities & International Trade", "item": "https://www.indravax.com/sectors/commodities-international-trade" },
                        ],
                    },
                ],
            }) }} />

            {/* Breadcrumb */}
            <div className="border-b border-[#C9A14A]/10" data-testid="commodities-breadcrumb">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-2 text-[0.65rem] tracking-[0.22em] uppercase text-slate-500">
                    <Link to="/" className="hover:text-[#C9A14A] transition-colors">Home</Link>
                    <ChevronRight size={12} className="text-[#C9A14A]/40" />
                    <Link to="/sectors" className="hover:text-[#C9A14A] transition-colors">Sectors</Link>
                    <ChevronRight size={12} className="text-[#C9A14A]/40" />
                    <span className="text-[#C9A14A]">Commodities & International Trade</span>
                </div>
            </div>

            {/* HERO */}
            <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="commodities-hero">
                <img
                    src="https://images.unsplash.com/photo-1605745341112-85968b19335b?auto=format&fit=crop&w=2400&q=80"
                    alt="Global trade — container port and shipping routes"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#040B1A]/88" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#040B1A] via-[#040B1A]/85 to-[#040B1A]/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <GlobeLines className="opacity-[0.12]" />
                <div className="grain-overlay" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <Ship size={16} className="text-[#C9A14A]" />
                            <span className="overline">Commodities & International Trade Vertical</span>
                        </div>
                        <h1 className="font-serif text-4xl md:text-6xl lg:text-[4.5rem] text-white leading-[1.05] tracking-tight">
                            Commodities & International Trade Advisory for{" "}
                            <span className="italic font-light text-[#C9A14A]">Structured Global Transactions.</span>
                        </h1>
                        <p className="mt-10 text-base md:text-lg text-slate-300 font-light leading-[1.85] max-w-3xl">
                            Indravax Strategic Advisory LLP supports commodity and international trade opportunities
                            through strategic sourcing, supplier-buyer coordination, mandate validation, documentation
                            support, commercial procedure alignment and cross-border execution coordination.
                        </p>
                        <p className="mt-5 text-base md:text-lg text-slate-300 font-light leading-[1.85] max-w-3xl">
                            We work with serious counterparties where product clarity, quantity, pricing, inspection,
                            logistics, payment discipline, documentation and counterparty credibility are essential.
                        </p>
                        <p className="mt-3 text-[0.7rem] tracking-[0.3em] uppercase text-[#C9A14A]">
                            Sourcing · Documentation · Cross-Border Execution
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4">
                            <a href="#submit" className="btn-primary group" data-testid="commodities-hero-submit">
                                Submit Commodity Requirement
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </a>
                            <Link to="/contact" className="btn-secondary" data-testid="commodities-hero-supply">
                                Discuss Supply Opportunity
                            </Link>
                            <Link to="/contact" className="btn-secondary" data-testid="commodities-hero-review">
                                Request Trade Transaction Review
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* OVERVIEW */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="commodities-overview">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-4">
                        <Overline>Overview</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            More than introductions. <em className="text-[#C9A14A] not-italic">Verified procedure.</em>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-[1.9]">
                        <p>
                            Indravax supports structured commodity and international trade engagements involving
                            buyers, suppliers, exporters, importers, trading houses, mandate holders, logistics
                            providers and institutional procurement channels.
                        </p>
                        <p>
                            Commodity transactions require more than introduction. They require
                            <span className="text-[#C9A14A]"> verified counterparties, product clarity, realistic
                            pricing, documentation readiness, inspection standards, payment alignment, shipping
                            coordination and compliance-sensitive execution.</span>
                        </p>
                        <p>
                            Indravax assists in organising the commercial framework required to move commodity
                            opportunities from enquiry to structured engagement.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* FOCUS AREAS */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="commodities-focus">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Commodity Focus Areas"
                        title="Six commodity verticals."
                        description="Indravax engages selectively across commodity segments where our network and advisory framework add material value."
                        testId="commodities-focus-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {COMMODITY_FOCUS.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <FadeUp key={c.title} delay={i * 0.06}>
                                    <article className="institutional-card h-full flex flex-col" data-testid={`commodities-focus-${i}`}>
                                        <div className="flex items-start justify-between">
                                            <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                            <span className="text-[0.65rem] tracking-[0.3em] text-slate-500 uppercase">0{i + 1}</span>
                                        </div>
                                        <h3 className="font-serif text-2xl text-white mt-6 leading-tight">{c.title}</h3>
                                        <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{c.desc}</p>
                                        <div className="mt-6">
                                            <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-2">Products</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {c.products.map((p) => (
                                                    <span key={p} className="px-2.5 py-1 border border-[#C9A14A]/30 text-[0.65rem] tracking-[0.15em] uppercase text-slate-300">
                                                        {p}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mt-5 pt-5 border-t border-[#C9A14A]/15">
                                            <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-2">Transaction Notes</div>
                                            <ul className="space-y-1.5">
                                                {c.notes.map((n) => (
                                                    <li key={n} className="text-xs text-slate-400 font-light flex items-baseline gap-2">
                                                        <span className="h-px w-2 bg-[#C9A14A] shrink-0 translate-y-2" />
                                                        <span>{n}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </article>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TRANSACTION STRUCTURES */}
            <section className="py-24 md:py-32" data-testid="commodities-structures">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Transaction Structures"
                        title="Commodity transaction structures we support."
                        description="Actual transaction structure depends on product type, jurisdiction, seller procedure, buyer capability, payment instrument, port logistics, inspection requirements and applicable law."
                        testId="commodities-structures-heading"
                    />
                    <FadeUp>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="commodities-structures-grid">
                            {STRUCTURES.map((s, i) => (
                                <div
                                    key={s}
                                    className="border border-[#C9A14A]/20 bg-[#0A1F44]/30 p-4 hover:border-[#C9A14A]/60 hover:bg-[#0A1F44]/60 transition-all duration-500 flex items-center gap-3"
                                    data-testid={`commodities-structure-${i}`}
                                >
                                    <span className="text-[0.65rem] tracking-[0.25em] uppercase text-[#C9A14A] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-sm text-slate-200 font-light leading-tight">{s}</span>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* OUR ROLE */}
            <section className="py-24 md:py-32 bg-[#07132E]/40 relative overflow-hidden" data-testid="commodities-role">
                <GlobeLines className="opacity-[0.06]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Our Role</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Our role in commodities & international trade engagements.
                        </h2>
                        <p className="text-base text-slate-400 mt-6 font-light leading-relaxed">
                            Indravax supports commodity transactions through advisory, validation, structuring,
                            documentation coordination and execution follow-up — within applicable legal frameworks.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <ul className="border-t border-[#C9A14A]/20 grid grid-cols-1 sm:grid-cols-2" data-testid="commodities-role-list">
                            {ROLE_POINTS.map((p, i) => (
                                <li key={p} className={`flex items-baseline gap-4 py-3.5 border-b border-[#C9A14A]/15 ${i % 2 === 0 ? "sm:border-r sm:pr-6" : "sm:pl-6"}`}>
                                    <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-sm text-slate-200 font-light leading-relaxed">{p}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-24 md:py-32" data-testid="commodities-process">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="How We Engage"
                        title="A seven-stage trade engagement process."
                        description="From requirement intake through commercial closure, every engagement follows the same disciplined progression."
                        testId="commodities-process-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-[#C9A14A]/20">
                        {PROCESS.map((s, i) => (
                            <FadeUp
                                key={s.n}
                                delay={i * 0.05}
                                className={`p-8 md:p-10 border-b border-[#C9A14A]/20 ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""} ${(i + 1) % 2 !== 0 ? "md:border-r lg:border-r" : ""}`}
                            >
                                <div className="font-serif text-5xl text-[#C9A14A] italic">{s.n}</div>
                                <h3 className="font-serif text-2xl text-white mt-6 leading-tight">{s.t}</h3>
                                <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{s.d}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMPLIANCE */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="commodities-compliance">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <Lock size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Compliance-Led Trade Support</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Documented. Verified. Procedure-led.
                            </h3>
                            <p className="mt-6 text-slate-300 font-light leading-[1.9]">
                                Commodity and international trade transactions require documentary discipline,
                                product clarity, origin clarity, payment alignment, logistics planning and credible
                                counterparties. Indravax supports only structured, lawful and properly documented
                                engagements — subject to applicable laws, import-export regulations, sanctions
                                restrictions, banking requirements and jurisdictional compliance.
                            </p>
                            <p className="mt-6 text-xs text-slate-500 italic font-light border-l-2 border-[#C9A14A]/40 pl-5 leading-relaxed">
                                Indravax does not provide legal, tax, banking, customs or sanctions certification.
                                Parties should obtain independent professional advice and conduct their own due
                                diligence before entering into any transaction.
                            </p>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <ShieldCheck size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Compliance Pillars</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Fourteen disciplines we hold firm.
                            </h3>
                            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6" data-testid="commodities-compliance-list">
                                {COMPLIANCE_POINTS.map((c) => (
                                    <li key={c} className="flex items-start gap-3 pb-3 border-b border-[#C9A14A]/10">
                                        <span className="h-px w-3 bg-[#C9A14A] mt-3 shrink-0" />
                                        <span className="text-sm text-slate-300 font-light">{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* DOCUMENTS */}
            <section className="py-24 md:py-32" data-testid="commodities-documents">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Documentation"
                        title="Documents commonly required in commodity transactions."
                        description="Document requirements vary depending on product, transaction procedure, jurisdiction, port, payment terms, seller policy, buyer capability and applicable law."
                        testId="commodities-documents-heading"
                    />
                    <FadeUp>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 border-t border-[#C9A14A]/20 pt-8">
                            {DOCUMENTS.map((d, i) => (
                                <div key={d} className="flex items-center gap-3 py-2.5 border-b border-[#C9A14A]/10" data-testid={`commodities-doc-${i}`}>
                                    <FileSignature size={14} className="text-[#C9A14A] shrink-0" />
                                    <span className="text-sm text-slate-300 font-light">{d}</span>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* WHO */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="commodities-who">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Who Should Engage With Us</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Built for serious commercial counterparties.
                        </h2>
                        <p className="text-base text-slate-400 mt-6 font-light leading-relaxed">
                            Our engagement model is suited for counterparties that are serious, documented,
                            commercially ready and willing to work through proper procedure.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="lg:col-span-7">
                        <ul className="border-t border-[#C9A14A]/20" data-testid="commodities-who-list">
                            {WHO.map((w, i) => (
                                <li key={w} className="flex items-baseline gap-5 py-3.5 border-b border-[#C9A14A]/15">
                                    <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-base text-slate-200 font-light leading-relaxed">{w}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>
            </section>

            {/* RISK AWARENESS */}
            <section className="py-24 md:py-32" data-testid="commodities-risk">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Trade Risk Awareness"
                        title="Commodity trade requires discipline and verification."
                        description="International commodity transactions often involve complex procedures, multiple intermediaries, price volatility, documentation risks, logistics dependencies, banking requirements and significant financial exposure."
                        testId="commodities-risk-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {RISK_PILLARS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <FadeUp key={p.t} delay={i * 0.06}>
                                    <div className="institutional-card h-full" data-testid={`commodities-risk-${i}`}>
                                        <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                        <h3 className="font-serif text-xl text-white mt-6 leading-tight">{p.t}</h3>
                                        <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{p.d}</p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="mt-12 text-sm text-slate-400 font-light leading-[1.9] max-w-3xl border-l-2 border-[#C9A14A]/40 pl-5 italic">
                            Indravax encourages serious counterparties to avoid unsupported claims, unrealistic
                            prices, unverifiable supply, undocumented mandate chains, unclear payment procedures
                            and incomplete specifications.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* SUBMIT COMMODITY FORM */}
            <section id="submit" className="py-24 md:py-32 bg-[#07132E]/40 scroll-mt-28" data-testid="commodities-submit">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        align="center"
                        overline="Initiate a Trade Engagement"
                        title="Submit a commodity & international trade requirement."
                        description="All commodity enquiries are reviewed for commercial seriousness, documentation readiness, counterparty credibility, product clarity and compliance suitability."
                        testId="commodities-submit-heading"
                    />
                    <FadeUp>
                        <form
                            onSubmit={onSubmit}
                            encType="multipart/form-data"
                            className="institutional-card p-8 md:p-12"
                            data-testid="commodities-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Full Name *</label>
                                    <input type="text" name="name" value={form.name} onChange={onChange} className="institutional-input" placeholder="Full name" required data-testid="commodities-form-name" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Company Name</label>
                                    <input type="text" name="company" value={form.company} onChange={onChange} className="institutional-input" placeholder="Organisation" data-testid="commodities-form-company" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Designation</label>
                                    <input type="text" name="designation" value={form.designation} onChange={onChange} className="institutional-input" placeholder="e.g. Director, Principal" data-testid="commodities-form-designation" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Country</label>
                                    <input type="text" name="country" value={form.country} onChange={onChange} className="institutional-input" placeholder="Country" data-testid="commodities-form-country" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Email Address *</label>
                                    <input type="email" name="email" value={form.email} onChange={onChange} className="institutional-input" placeholder="you@org.com" required data-testid="commodities-form-email" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">WhatsApp / Phone</label>
                                    <input type="tel" name="phone" value={form.phone} onChange={onChange} className="institutional-input" placeholder="+..." data-testid="commodities-form-phone" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Type of Counterparty</label>
                                    <select name="counterparty_type" value={form.counterparty_type} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="commodities-form-counterparty">
                                        {COUNTERPARTY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Commodity Category</label>
                                    <select name="commodity_category" value={form.commodity_category} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="commodities-form-category">
                                        {COMMODITY_CATEGORY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Product Name *</label>
                                    <input type="text" name="product_name" value={form.product_name} onChange={onChange} className="institutional-input" placeholder="e.g. Urea Granular 46% N" required data-testid="commodities-form-product" />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Product Grade / Specification</label>
                                    <textarea name="product_specification" value={form.product_specification} onChange={onChange} className="institutional-input" placeholder="Grade, purity, moisture, key parameters." rows={3} data-testid="commodities-form-spec" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Quantity Required / Available</label>
                                    <input type="text" name="quantity" value={form.quantity} onChange={onChange} className="institutional-input" placeholder="e.g. 50,000 MT / month" data-testid="commodities-form-quantity" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Packing Details</label>
                                    <input type="text" name="packing_details" value={form.packing_details} onChange={onChange} className="institutional-input" placeholder="e.g. 50 kg PP bags, jumbo bags, bulk" data-testid="commodities-form-packing" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Delivery Terms</label>
                                    <select name="delivery_terms" value={form.delivery_terms} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="commodities-form-delivery">
                                        {DELIVERY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Origin</label>
                                    <input type="text" name="origin" value={form.origin} onChange={onChange} className="institutional-input" placeholder="Country of origin" data-testid="commodities-form-origin" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Port of Loading</label>
                                    <input type="text" name="port_loading" value={form.port_loading} onChange={onChange} className="institutional-input" placeholder="e.g. Jebel Ali, Rotterdam" data-testid="commodities-form-port-loading" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Port of Discharge</label>
                                    <input type="text" name="port_discharge" value={form.port_discharge} onChange={onChange} className="institutional-input" placeholder="e.g. JNPT, Mundra" data-testid="commodities-form-port-discharge" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Target / Offer Price</label>
                                    <input type="text" name="target_price" value={form.target_price} onChange={onChange} className="institutional-input" placeholder="e.g. USD 380 / MT CIF" data-testid="commodities-form-price" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Payment Terms</label>
                                    <input type="text" name="payment_terms" value={form.payment_terms} onChange={onChange} className="institutional-input" placeholder="e.g. LC at sight, DLC, TT" data-testid="commodities-form-payment" />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Transaction Procedure Available</label>
                                    <textarea name="transaction_procedure" value={form.transaction_procedure} onChange={onChange} className="institutional-input" placeholder="Seller / buyer procedure: LOI → ICPO → SCO → SPA → LC, etc." rows={3} data-testid="commodities-form-procedure" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Documents Available</label>
                                    <select name="documents_available" value={form.documents_available} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="commodities-form-docs">
                                        {DOCUMENTS_AVAILABLE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Timeline</label>
                                    <select name="timeline" value={form.timeline} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="commodities-form-timeline">
                                        {TIMELINE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Additional Message</label>
                                    <textarea name="message" value={form.message} onChange={onChange} className="institutional-input" placeholder="Specifications, timelines, jurisdictions, sensitivities." rows={3} data-testid="commodities-form-message" />
                                </div>

                                {/* FILE UPLOAD */}
                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Upload Documents (optional)</label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        name="file"
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                                        onChange={onFileChange}
                                        className="hidden"
                                        data-testid="commodities-form-file-input"
                                    />
                                    {!file ? (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full border border-dashed border-[#C9A14A]/40 hover:border-[#C9A14A] bg-[#040B1A]/50 transition-colors p-8 flex flex-col items-center justify-center gap-3 group"
                                            data-testid="commodities-form-file-pick"
                                        >
                                            <UploadCloud size={28} className="text-[#C9A14A] group-hover:scale-110 transition-transform" />
                                            <span className="text-sm text-slate-300 font-light">Click to attach a document</span>
                                            <span className="text-[0.65rem] tracking-[0.25em] uppercase text-slate-500">PDF · DOC · DOCX · PNG · JPG · max 10 MB</span>
                                        </button>
                                    ) : (
                                        <div className="w-full border border-[#C9A14A]/40 bg-[#040B1A]/60 px-5 py-4 flex items-center justify-between gap-4" data-testid="commodities-form-file-selected">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <UploadCloud size={20} className="text-[#C9A14A] shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="text-sm text-white truncate">{file.name}</div>
                                                    <div className="text-[0.7rem] text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                                                </div>
                                            </div>
                                            <button type="button" onClick={removeFile} className="text-slate-400 hover:text-[#C9A14A] transition-colors" data-testid="commodities-form-file-remove" aria-label="Remove file">
                                                <X size={18} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                                <p className="text-xs text-slate-500 max-w-lg leading-relaxed font-light flex items-start gap-2">
                                    <ScrollText size={14} className="text-[#C9A14A] mt-0.5 shrink-0" />
                                    All commodity and international trade enquiries are reviewed for commercial
                                    seriousness, documentation readiness, counterparty credibility, product clarity
                                    and compliance suitability.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="commodities-form-submit"
                                >
                                    {submitting ? (
                                        <><Loader2 size={14} className="animate-spin" /> Submitting</>
                                    ) : (
                                        <>Submit Commodity Requirement <Send size={14} /></>
                                    )}
                                </button>
                            </div>
                        </form>
                    </FadeUp>
                </div>
            </section>

            {/* OTHER SECTORS */}
            <section className="py-24 md:py-32" data-testid="commodities-other-sectors">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Explore Other Sectors"
                        title="Other Indravax verticals."
                        testId="commodities-other-heading"
                    />
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {OTHER_SECTORS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <FadeUp key={s.title} delay={i * 0.04}>
                                    <Link
                                        to={s.path}
                                        className="block border border-[#C9A14A]/20 bg-[#0A1F44]/30 p-5 hover:border-[#C9A14A]/60 hover:bg-[#0A1F44]/60 transition-all duration-500"
                                        data-testid={`commodities-other-${i}`}
                                    >
                                        <Icon size={22} strokeWidth={1.1} className="text-[#C9A14A]" />
                                        <div className="font-serif text-base text-white mt-3 leading-tight">{s.title}</div>
                                        <div className="mt-3 inline-flex items-center gap-1.5 text-[0.6rem] tracking-[0.22em] uppercase text-[#C9A14A]">
                                            Explore <ArrowRight size={12} />
                                        </div>
                                    </Link>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24 border-t border-[#C9A14A]/15 bg-[#07132E]/40" data-testid="commodities-final-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <SectionHeading
                        align="center"
                        overline="Engage"
                        title="Discuss a commodity or international trade opportunity."
                        description="Whether you represent supply, demand, sourcing, logistics, institutional procurement, investment or transaction documentation — Indravax can support structured engagement across commodity and international trade opportunities."
                        testId="commodities-final-cta-heading"
                    />
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="#submit" className="btn-primary" data-testid="commodities-final-submit">
                            Submit Commodity Requirement <ArrowRight size={14} />
                        </a>
                        <Link to="/contact" className="btn-secondary" data-testid="commodities-final-review">
                            Request Trade Transaction Review
                        </Link>
                        <Link to="/contact" className="btn-secondary" data-testid="commodities-final-contact">
                            Contact Indravax
                        </Link>
                    </div>
                </div>
            </section>

            {/* CONTACT + DISCLAIMER */}
            <section className="py-20 border-t border-[#C9A14A]/15" data-testid="commodities-contact">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div className="flex gap-3 text-sm text-slate-400">
                        <MapPin size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <span>Indravax Strategic Advisory LLP<br />New Delhi, India</span>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Phone size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="tel:+917814050036" className="hover:text-[#C9A14A] transition-colors">+91 78140 50036</a>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Mail size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="mailto:prateekgrewal@indravax.com" className="hover:text-[#C9A14A] transition-colors">prateekgrewal@indravax.com</a>
                    </div>
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
                    <p className="text-xs text-slate-500 font-light leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic">
                        Indravax Strategic Advisory LLP provides advisory and facilitation services across
                        commodity and international trade engagements. The firm does not act as a principal
                        buyer, seller, broker, exchange member or licensed customs / trade-finance institution
                        unless specifically authorised under applicable laws, licences and written agreements.
                        Engagements are subject to applicable import-export regulations, sanctions frameworks,
                        banking and customs requirements.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default SectorsCommodities;
